import * as React from 'react';

import { useOrdering } from 'root/apps/PurchaseOrder/ShipmentHistory/hooks/use-ordering';

type Pagination = {
  count: number;
  perPage: number;
  start: number;
  isFirst: boolean;
  isLast: boolean;
  rangeLabel: string;
};

type PaginationAction = 'next' | 'previous';

const pageReducer = (state: Pagination, action: PaginationAction): Pagination => {
  let newStart = state.start;
  if (action === 'next' && !state.isLast) {
    newStart = state.start + state.perPage;
  } else if (action === 'previous' && !state.isFirst) {
    newStart = state.start - state.perPage;
  }

  const minEnd = Math.min(newStart + 1 + state.perPage, state.count + 1);

  return {
    ...state,
    start: newStart,
    isFirst: newStart === 0,
    isLast: newStart + state.perPage >= state.count,
    rangeLabel: state.count > 0 ? `${newStart + 1} - ${minEnd - 1}` : '0',
  };
};

// To sort on a given key on a type
const buildDescendingSorter =
  <T>(key: keyof T) =>
  (a: T, b: T) =>
    a[key] < b[key] ? 1 : -1;
const buildAscendingSorter =
  <T>(key: keyof T) =>
  (a: T, b: T) =>
    a[key] > b[key] ? 1 : -1;

export function useTableDataSet<T>(data: T[], perPage = 10) {
  const [rows, setRows] = React.useState(data?.slice ? data.slice(0, perPage) : []);
  const { orderedBy, orderDirection, getOrderDirection, setOrderDirection } = useOrdering();
  const [pagination, changePagination] = React.useReducer(pageReducer, {
    count: data.length,
    perPage,
    start: 0,
    isFirst: true,
    isLast: data.length <= perPage,
    rangeLabel: data.length ? `1 - ${Math.min(perPage, data.length)}` : '0',
  });

  React.useEffect(() => {
    if (!data.length) return;

    if (orderDirection === 'unsorted' || !orderedBy) {
      setRows(data.slice(pagination.start, pagination.start + pagination.perPage));
      return;
    }

    const sortedRows = [...data];
    const sorterBuilder = orderDirection === 'desc' ? buildDescendingSorter : buildAscendingSorter;
    sortedRows.sort(sorterBuilder(orderedBy as keyof T));
    setRows(sortedRows.slice(pagination.start, pagination.start + pagination.perPage));
  }, [orderedBy, orderDirection, pagination, data]);

  return {
    rows,
    pagination,
    changePagination,
    getOrderDirection,
    setOrderDirection,
  };
}
